import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Table, Badge, Alert, Modal, Form, Spinner, Row, Col } from 'react-bootstrap';
import {
  ArrowPathIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  PauseCircleIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import { recurringAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { RecurringOrder } from '../../types';
import Header from '../common/Header';
import Footer from '../common/Footer';

const frequencyLabels: Record<string, string> = {
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  monthly: 'Monthly',
};

const RecurringOrders: React.FC = () => {
  const navigate = useNavigate();
  const { user, isExpired } = useAuth();
  const [orders, setOrders] = useState<RecurringOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editOrder, setEditOrder] = useState<RecurringOrder | null>(null);
  const [editForm, setEditForm] = useState({ frequency: '', next_delivery_date: '', notes: '', endType: 'never' as 'never' | 'date', end_date: '' });
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const leadDays = (user as any)?.min_delivery_lead_days ?? 0;
  const minDeliveryDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + leadDays);
    return d.toISOString().split('T')[0];
  })();

  const fetchOrders = useCallback(async () => {
    try {
      const res = await recurringAPI.getRecurringOrders();
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('Failed to load recurring orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const handleToggleActive = async (ro: RecurringOrder) => {
    try {
      await recurringAPI.updateRecurringOrder(ro.id, { is_active: !ro.is_active });
      setOrders(prev => prev.map(o => o.id === ro.id ? { ...o, is_active: !o.is_active } : o));
      setSuccess(`Recurring order ${ro.is_active ? 'paused' : 'resumed'}.`);
    } catch {
      setError('Failed to update recurring order.');
    }
  };

  const handleEditOpen = (ro: RecurringOrder) => {
    setEditOrder(ro);
    setEditForm({
      frequency: ro.frequency,
      next_delivery_date: ro.next_delivery_date,
      notes: ro.notes || '',
      endType: ro.end_date ? 'date' : 'never',
      end_date: ro.end_date || '',
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editOrder) return;
    setSaving(true);
    try {
      const res = await recurringAPI.updateRecurringOrder(editOrder.id, {
        frequency: editForm.frequency,
        next_delivery_date: editForm.next_delivery_date,
        notes: editForm.notes,
        end_date: editForm.endType === 'date' ? editForm.end_date : null,
      });
      setOrders(prev => prev.map(o => o.id === editOrder.id ? res.data : o));
      setShowEditModal(false);
      setSuccess('Recurring order updated.');
    } catch {
      setError('Failed to update recurring order.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await recurringAPI.deleteRecurringOrder(deleteId);
      setOrders(prev => prev.filter(o => o.id !== deleteId));
      setDeleteId(null);
      setSuccess('Recurring order deleted.');
    } catch {
      setError('Failed to delete recurring order.');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return `${m}/${d}/${y}`;
  };

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5 page-content-padding">
          <div className="text-center mb-3 mb-md-5">
            <h1 className="display-4 fw-bold text-primary mb-2 d-flex align-items-center justify-content-center gap-2">
              <ArrowPathIcon className="page-header-icon" style={{ width: '40px', height: '40px' }} />
              Recurring Orders
            </h1>
            <p className="lead text-muted mb-0">Manage your automatic repeat orders</p>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

          <Card className="professional-card">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 d-flex align-items-center gap-2">
                <ArrowPathIcon style={{ width: '20px', height: '20px' }} />
                Your Recurring Orders
              </h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-4">
                  <h5>No recurring orders</h5>
                  <p className="text-muted">
                    You can set up recurring orders from your Order History —
                    click the <ArrowPathIcon style={{ width: '16px', height: '16px', display: 'inline' }} /> button on any order.
                  </p>
                  <Button variant="primary" onClick={() => navigate('/orders')}>
                    Go to Order History
                  </Button>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="d-none d-md-block">
                    <Table striped hover responsive>
                      <thead>
                        <tr>
                          <th>Items</th>
                          <th>Frequency</th>
                          <th>Next Delivery</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map(ro => (
                          <tr key={ro.id}>
                            <td>
                              {ro.items && ro.items.length > 0 ? (
                                <ul className="list-unstyled mb-0">
                                  {ro.items.map(item => (
                                    <li key={item.id} className="small">
                                      {item.product.name} x{item.quantity}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-muted">No items</span>
                              )}
                            </td>
                            <td>
                              <Badge bg="primary" className="rounded-pill">
                                {frequencyLabels[ro.frequency] || ro.frequency}
                              </Badge>
                            </td>
                            <td>
                              {formatDate(ro.next_delivery_date)}
                              <br />
                              <small className="text-muted">
                                {ro.end_date ? `Ends ${formatDate(ro.end_date)}` : 'Until cancelled'}
                              </small>
                            </td>
                            <td>
                              <Badge bg={ro.is_active ? 'success' : 'secondary'}>
                                {ro.is_active ? 'Active' : 'Paused'}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                <Button
                                  variant={ro.is_active ? 'outline-warning' : 'outline-success'}
                                  size="sm"
                                  disabled={isExpired}
                                  onClick={() => handleToggleActive(ro)}
                                  title={ro.is_active ? 'Pause' : 'Resume'}
                                >
                                  {ro.is_active
                                    ? <PauseCircleIcon style={{ width: '14px', height: '14px' }} />
                                    : <PlayCircleIcon style={{ width: '14px', height: '14px' }} />}
                                </Button>
                                <Button variant="outline-primary" size="sm" disabled={isExpired} onClick={() => handleEditOpen(ro)} title="Edit">
                                  <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                                </Button>
                                <Button variant="outline-danger" size="sm" disabled={isExpired} onClick={() => setDeleteId(ro.id)} title="Delete">
                                  <TrashIcon style={{ width: '14px', height: '14px' }} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Mobile Card Layout */}
                  <div className="d-md-none">
                    {orders.map(ro => (
                      <Card key={ro.id} className="mb-3 border" style={{ borderRadius: '12px' }}>
                        <Card.Body className="p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <Badge bg="primary" className="rounded-pill">
                              {frequencyLabels[ro.frequency] || ro.frequency}
                            </Badge>
                            <Badge bg={ro.is_active ? 'success' : 'secondary'}>
                              {ro.is_active ? 'Active' : 'Paused'}
                            </Badge>
                          </div>
                          {ro.items && ro.items.length > 0 && (
                            <div className="mb-2">
                              {ro.items.map(item => (
                                <small key={item.id} className="d-block text-muted">
                                  {item.product.name} x{item.quantity}
                                </small>
                              ))}
                            </div>
                          )}
                          <small className="text-muted d-block mb-2">
                            Next delivery: {formatDate(ro.next_delivery_date)}
                            {' · '}
                            {ro.end_date ? `Ends ${formatDate(ro.end_date)}` : 'Until cancelled'}
                          </small>
                          <div className="d-flex gap-2">
                            <Button
                              variant={ro.is_active ? 'outline-warning' : 'outline-success'}
                              size="sm"
                              disabled={isExpired}
                              onClick={() => handleToggleActive(ro)}
                            >
                              {ro.is_active ? 'Pause' : 'Resume'}
                            </Button>
                            <Button variant="outline-primary" size="sm" disabled={isExpired} onClick={() => handleEditOpen(ro)}>
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" disabled={isExpired} onClick={() => setDeleteId(ro.id)}>
                              Delete
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>

          <div className="mt-3">
            <Button variant="outline-secondary" onClick={() => navigate('/orders')}>
              Back to Order History
            </Button>
          </div>
        </Container>
      </div>
      <Footer />

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Recurring Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Frequency</Form.Label>
            <Form.Select
              value={editForm.frequency}
              onChange={e => setEditForm(prev => ({ ...prev, frequency: e.target.value }))}
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-Weekly</option>
              <option value="monthly">Monthly</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Next Delivery Date</Form.Label>
            <Form.Control
              type="date"
              min={minDeliveryDate}
              value={editForm.next_delivery_date}
              onChange={e => setEditForm(prev => ({ ...prev, next_delivery_date: e.target.value }))}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Duration</Form.Label>
            <div>
              <Form.Check
                type="radio"
                id="edit-endType-never"
                label="Until I cancel"
                checked={editForm.endType === 'never'}
                onChange={() => setEditForm(prev => ({ ...prev, endType: 'never', end_date: '' }))}
              />
              <Form.Check
                type="radio"
                id="edit-endType-date"
                label="Until a specific date"
                checked={editForm.endType === 'date'}
                onChange={() => setEditForm(prev => ({ ...prev, endType: 'date' }))}
              />
            </div>
            {editForm.endType === 'date' && (
              <Form.Control
                type="date"
                className="mt-2"
                min={editForm.next_delivery_date || minDeliveryDate}
                value={editForm.end_date}
                onChange={e => setEditForm(prev => ({ ...prev, end_date: e.target.value }))}
              />
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={editForm.notes}
              onChange={e => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button variant="primary" disabled={saving} onClick={handleEditSave}>
            {saving ? <><Spinner size="sm" className="me-2" />Saving...</> : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal show={deleteId !== null} onHide={() => setDeleteId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Recurring Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this recurring order? This cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="danger" disabled={deleting} onClick={handleDelete}>
            {deleting ? <><Spinner size="sm" className="me-2" />Deleting...</> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RecurringOrders;
